import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ImgDbZoomService } from 'src/app/img-db/services/img-db-zoom.service';
import { ImgAnnotation } from '../../img-db/model/img-annotation';
import { ImgWithSelection } from '../../img-db/model/img-with-selection';
import { PagingResult } from '../../shared/models/pageable';
import { ColorService } from '../../shared/services/color.service';
import { isVisualEvalAnnotation } from '../models/visual-eval-data-set';
import {
    ImageViewerStorageService,
    ShowNameMarker,
} from '../service/image-viewer-storage.service';
import {
    CanvasBox,
    toCanvasBox,
} from '../../shared/image-canvas/image-canvas-viewport.component';

/**
 * Canvas evaluation image viewer. Draws ground truth and predictions on the
 * shared image canvas.
 */
@Component({
    selector: 'app-image-canvas-viewer',
    templateUrl: './image-canvas-viewer.component.html',
    styleUrls: ['./image-canvas-viewer.component.scss'],
    providers: [ImageViewerStorageService],
    standalone: false,
})
export class ImageCanvasViewerComponent implements OnInit, OnDestroy {
    readonly PAGE_SIZE: number = 8;

    @Input() trainingId: string;
    @Input() selectedImageIndexInAllImages: number;

    readonly minZoomLevel = ImgDbZoomService.MIN_IMAGE_ZOOM;
    readonly maxZoomLevel = ImgDbZoomService.MAX_IMAGE_ZOOM;

    images: ImgWithSelection[] = [];
    selectedImage: ImgWithSelection;
    isLeftArrowVisible: boolean;
    isRightArrowVisible: boolean;
    totalPages: number;
    totalElements: number;
    currentImageNumber: number = 1;
    currentPage: number = 1;
    pagingTimeout: ReturnType<typeof setTimeout>; //timeout paging debouncing (fetch next page after certain time)

    /**
     * Boxes bound to the viewport. Rebuilt only when annotations or label
     * visibility change, so the viewport gets a stable array between changes.
     */
    evaluationCanvasBoxes: CanvasBox[] = [];

    private currentAnnotations: ImgAnnotation[];

    zoomControl: FormControl<number> = new FormControl(this.minZoomLevel);
    currentZoomLevel: number = this.minZoomLevel;

    private subscriptions: Subscription[] = [];

    destroy$: Subject<void> = new Subject<void>();

    showNames: ShowNameMarker = {
        showPreTrainName: true,
        showPostTrainName: true,
    };

    showData: boolean = false;

    constructor(
        private colorService: ColorService,
        private imageViewerStorageService: ImageViewerStorageService
    ) {
        this.isLeftArrowVisible = false;
        this.isRightArrowVisible = false;
    }

    get annotations(): ImgAnnotation[] {
        return this.currentAnnotations;
    }

    set annotations(annotations: ImgAnnotation[]) {
        this.currentAnnotations = annotations;
        this.rebuildCanvasBoxes();
    }

    ngOnInit() {
        if (!this.selectedImageIndexInAllImages) {
            this.selectedImageIndexInAllImages = 0;
        }
        this.currentPage = this.calculatePageNumberFromImageIndexInAllImages(
            this.selectedImageIndexInAllImages
        );
        this.imageViewerStorageService.currentPage = this.currentPage;
        this.imageViewerStorageService.init(this.trainingId);
        this.subscriptions.push(
            this.imageViewerStorageService
                .getImages()
                .pipe(
                    take(1),
                    switchMap((page: PagingResult<ImgWithSelection>) => {
                        this.initializeData(page);
                        return this.imageViewerStorageService.annotations$.pipe(
                            takeUntil(this.destroy$)
                        );
                    }),
                    tap((annotations: ImgAnnotation[]) => {
                        this.annotations = annotations;
                    })
                )
                .subscribe()
        );
        this.subscriptions.push(
            this.imageViewerStorageService.showNameMarkers$.subscribe(
                (showNames: ShowNameMarker) => {
                    this.showNames.showPreTrainName =
                        showNames.showPreTrainName;
                    this.showNames.showPostTrainName =
                        showNames.showPostTrainName;
                    this.rebuildCanvasBoxes();
                }
            )
        );
    }

    private initializeData(page: PagingResult<ImgWithSelection>) {
        if (page?.values?.length) {
            this.showData = true;
            this.images = page.values;
            this.currentImageNumber = this.selectedImageIndexInAllImages + 1;
            const indexInPage =
                this.selectedImageIndexInAllImages -
                this.PAGE_SIZE * (this.currentPage - 1);
            this.selectImage(indexInPage);
            this.totalElements = page.totalElements;
            this.totalPages = page.totalPages;
            this.checkArrowsAvailability();
        }
    }

    ngOnDestroy(): void {
        clearTimeout(this.pagingTimeout);
        this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
        this.destroy$.next();
        this.destroy$.complete();
    }

    private changeZoom(zoomLevel: number) {
        if (
            zoomLevel > this.maxZoomLevel ||
            zoomLevel < this.minZoomLevel ||
            !this.selectedImage
        ) {
            return;
        }
        this.onCanvasLevelChange(zoomLevel);
    }

    onSliderZoomChange() {
        const zoomLevel = this.zoomControl.value;
        this.changeZoom(zoomLevel);
    }

    onZoomIn() {
        const zoomLevel = this.zoomControl.value + 1;
        this.changeZoom(zoomLevel);
    }

    onZoomOut() {
        const zoomLevel = this.zoomControl.value - 1;
        this.changeZoom(zoomLevel);
    }

    onCanvasLevelChange(level: number): void {
        this.currentZoomLevel = level;
        this.zoomControl.setValue(level);
    }

    private rebuildCanvasBoxes(): void {
        this.evaluationCanvasBoxes = (this.currentAnnotations ?? []).map(
            (annotation) => {
                const model = isVisualEvalAnnotation(annotation);
                const showLabel = model
                    ? this.showNames.showPostTrainName
                    : this.showNames.showPreTrainName;
                return toCanvasBox(annotation, {
                    color: this.getColorByAnnotation(annotation),
                    label: showLabel
                        ? this.getImgCategoryName(annotation.categoryId)
                        : undefined,
                    labelBelow: model,
                    dashed: model,
                });
            }
        );
    }

    selectImage(indexInPage: number) {
        const img = this.images[indexInPage];
        if (this.selectedImage?.id !== img?.id) {
            this.selectedImage = img;
            this.imageViewerStorageService.selectedImageIndex$.next(
                indexInPage + this.PAGE_SIZE * (this.currentPage - 1)
            );
        }
    }

    getColor(categoryName: string): string {
        return this.colorService.getColor(categoryName);
    }

    getColorByAnnotation(annotation: ImgAnnotation): string {
        return this.getColor(this.getImgCategoryName(annotation.categoryId));
    }

    getImgCategoryName(id: string): string {
        return this.imageViewerStorageService.categories.get(id);
    }

    getNextPage() {
        if (this.currentPage + 1 <= this.totalPages) {
            this.currentPage++;
            this.imageViewerStorageService.currentPage = this.currentPage;
            this.getNewPage();
        }
    }

    getPrevPage() {
        if (this.currentPage - 1 >= 1) {
            this.currentPage--;
            this.imageViewerStorageService.currentPage = this.currentPage;
            this.getNewPage();
        }
    }

    getNewPage(indexOfImageToBeSelected?: number) {
        // Timeout to prevent unnecessary requests when user clicks repeatedly
        clearTimeout(this.pagingTimeout);
        this.pagingTimeout = setTimeout(() => {
            this.imageViewerStorageService
                .getImages()
                .pipe(take(1))
                .subscribe((page: PagingResult<ImgWithSelection>) => {
                    this.images = page.values;
                    this.checkArrowsAvailability();
                    if (
                        typeof indexOfImageToBeSelected === 'number' &&
                        indexOfImageToBeSelected >= 0
                    ) {
                        this.selectImage(indexOfImageToBeSelected);
                    }
                });
        }, 300);
    }

    private checkArrowsAvailability() {
        this.isRightArrowVisible = this.currentPage < this.totalPages;
        this.isLeftArrowVisible = this.currentPage > 1;
    }

    onNextValue(numberProvidedFromPagingBar: number) {
        if (this.currentImageNumber !== numberProvidedFromPagingBar) {
            this.currentImageNumber = numberProvidedFromPagingBar;

            const indexOfImageToBeSelected: number =
                this.findIndexOfImageToBeSelected(this.currentImageNumber) - 1;

            const newPage: number = this.calculateNextPage(
                numberProvidedFromPagingBar
            );
            if (newPage !== this.currentPage) {
                this.currentPage = newPage;
                this.imageViewerStorageService.currentPage = this.currentPage;
                this.getNewPage(indexOfImageToBeSelected);
            } else {
                this.selectImage(indexOfImageToBeSelected);
            }
        }
    }

    onSelectImage(indexOfSelectedImage: number) {
        this.selectImage(indexOfSelectedImage);
        this.currentImageNumber = this.findImageIndexToProvide();
    }

    private findImageIndexToProvide(): number {
        const idx: number = this.images.findIndex(
            (img: ImgWithSelection) => img.id === this.selectedImage.id
        );

        return this.calculateImageIndexInAllImagesFromImageIndexInPage(idx) + 1;
    }

    private calculateNextPage(val: number): number {
        return Math.ceil(val / this.PAGE_SIZE);
    }

    private calculatePageNumberFromImageIndexInAllImages(
        imageIndexInAllImages: number
    ): number {
        return Math.floor(imageIndexInAllImages / this.PAGE_SIZE) + 1;
    }

    private calculateImageIndexInAllImagesFromImageIndexInPage(
        imageIndexInPage: number
    ): number {
        return imageIndexInPage + this.PAGE_SIZE * (this.currentPage - 1);
    }

    private findIndexOfImageToBeSelected(imageToGet: number): number {
        return imageToGet % this.PAGE_SIZE || this.PAGE_SIZE;
    }
}
